import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useGetProductsLimitsFiltersQuery,
  useGetProductsLimitsQuery,
} from '@app/api/Products';
import type { ModelProductLimit } from '@app/api/Products';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import {
  initialPagination,
  initialFilters,
  getPurchasePlanningSettingsColumns,
} from '@app/pages/Warehouses/PurchasePlanningSettings/data';
import type {
  FiltersChangeType,
  PaginationInterface,
  ProductsLimitsFilters,
} from '@app/pages/Warehouses/PurchasePlanningSettings/types';
import { filterProductLimits } from '@app/pages/Warehouses/PurchasePlanningSettings/utils';
import { getOptions } from '@app/pages/Warehouses/utils';
import { useErrorHandler } from '@app/shared/hooks';
import type { Options } from '@app/types';

import type { PaginationProps } from 'antd';

export const usePurchasePlanningSettings = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const {
    userAccessRights: { Warehouse, Products },
  } = useNewUserAccessRights();

  const [filters, setFilters] = useState<ProductsLimitsFilters>(initialFilters);
  const [unsavedFilters, setUnsavedFilters] =
    useState<ProductsLimitsFilters>(initialFilters);

  const [primaryWarehouseId, setPrimaryWarehouseId] = useState<number | null>(
    null
  );
  const [pagination, setPagination] =
    useState<PaginationInterface>(initialPagination);

  const [productCategoriesOptions, setProductCategoriesOptions] =
    useState<Options>([]);
  const [warehousesOptions, setWarehousesOptions] = useState<Options>([]);

  const [rows, setRows] = useState<ModelProductLimit[]>([]);

  const [stockControlRow, setStockControlRow] =
    useState<ModelProductLimit | null>(null);

  const { warehouseId } = filters;

  const {
    data: productsLimitsFiltersData,
    isLoading: isProductsLimitsFiltersLoading,
    isFetching: isProductsLimitsFiltersFetching,
    isUninitialized: isProductsLimitsFiltersUninitialized,
    error: productsLimitsFiltersError,
    isError: isProductsLimitsFiltersError,
  } = useGetProductsLimitsFiltersQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId, refetchOnMountOrArgChange: true }
  );

  const {
    data: productsLimitsData,
    isLoading: isProductsLimitsLoading,
    isFetching: isProductsLimitsFetching,
    isUninitialized: isProductsLimitsUninitialized,
    error: productsLimitsError,
    isError: isProductsLimitsError,
  } = useGetProductsLimitsQuery(
    { ActiveBranchId, warehouseId },
    { skip: !ActiveBranchId || !warehouseId, refetchOnMountOrArgChange: true }
  );

  useErrorHandler([
    { flag: isProductsLimitsFiltersError, error: productsLimitsFiltersError },
    { flag: isProductsLimitsError, error: productsLimitsError },
  ]);

  const isFiltersDisabled = useMemo(
    () =>
      isProductsLimitsFiltersLoading ||
      isProductsLimitsFiltersFetching ||
      isProductsLimitsFiltersUninitialized,
    [
      isProductsLimitsFiltersLoading,
      isProductsLimitsFiltersFetching,
      isProductsLimitsFiltersUninitialized,
    ]
  );

  const isPageLoading = useMemo(
    () => isProductsLimitsLoading || isProductsLimitsUninitialized,
    [isProductsLimitsLoading, isProductsLimitsUninitialized]
  );

  const isFetching = useMemo(
    () => isProductsLimitsFetching,
    [isProductsLimitsFetching]
  );

  const isPageForbidden = useMemo(
    () => !Warehouse.IsOn || !Products.IsOn,
    [Warehouse, Products]
  );

  const range = useMemo(() => {
    const { page, itemsPerPage, total } = pagination;
    const from = total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
    const to = total === 0 ? 0 : Math.min(page * itemsPerPage, total);
    return { from, to };
  }, [pagination]);

  const getProductsLimitsFiltersDataHandler = useCallback(() => {
    if (!productsLimitsFiltersData) return;
    const { ProductCategories, Warehouses } = productsLimitsFiltersData;

    setProductCategoriesOptions(getOptions(ProductCategories));
    setWarehousesOptions(getOptions(Warehouses));

    const primaryWarehouse = Warehouses.find(({ IdS }) => IdS === 'primary');
    setPrimaryWarehouseId(primaryWarehouse ? primaryWarehouse.Id : null);

    const defaultWarehouseId = primaryWarehouse
      ? primaryWarehouse.Id
      : Warehouses[0]?.Id || 0;

    setFilters((prev) => ({ ...prev, warehouseId: defaultWarehouseId }));
    setUnsavedFilters((prev) => ({ ...prev, warehouseId: defaultWarehouseId }));
  }, [productsLimitsFiltersData]);

  useEffect(() => {
    setRows(productsLimitsData ?? []);
  }, [productsLimitsData]);

  const onFiltersChange: FiltersChangeType = useCallback((key, value) => {
    setUnsavedFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onSearch = useCallback(() => {
    setFilters(unsavedFilters);
    setPagination((p) => ({ ...p, page: 1 }));
  }, [unsavedFilters]);

  const onReset = useCallback(() => {
    const whId = primaryWarehouseId ?? 0;
    setPagination(initialPagination);
    setUnsavedFilters({ ...initialFilters, warehouseId: whId });
    setFilters({ ...initialFilters, warehouseId: whId });
  }, [primaryWarehouseId]);

  const categoryValueToLabel = useMemo(() => {
    const map = new Map<unknown, string>();
    productCategoriesOptions.forEach((o) =>
      map.set(o.value, String(o.label ?? ''))
    );
    return map;
  }, [productCategoriesOptions]);

  const filteredRows = useMemo(
    () =>
      filterProductLimits(rows, {
        search: filters.search,
        categories: filters.categories,
        categoryValueToLabel,
      }),
    [rows, filters.search, filters.categories, categoryValueToLabel]
  );

  const total = filteredRows.length;

  useEffect(() => {
    if (total === 0) {
      setPagination((p) => ({ ...p, total: 0, page: 1 }));
      return;
    }
    setPagination((p) => {
      const totalPages = Math.max(1, Math.ceil(total / p.itemsPerPage));
      const safePage = Math.min(p.page, totalPages);
      return { ...p, total, page: safePage };
    });
  }, [total]);

  const pageData = useMemo(() => {
    const start = (pagination.page - 1) * pagination.itemsPerPage;
    const end = start + pagination.itemsPerPage;
    return filteredRows.slice(start, end);
  }, [filteredRows, pagination.page, pagination.itemsPerPage]);

  const onPaginate: PaginationProps['onChange'] = (page, pageSize) => {
    setPagination((prev) => ({ ...prev, page, itemsPerPage: pageSize }));
  };

  const onPageSizeChange = useCallback((itemsPerPage: number) => {
    setPagination((p) => ({ ...p, itemsPerPage, page: 1 }));
  }, []);

  const openStockControlModal = useCallback((record: ModelProductLimit) => {
    setStockControlRow(record);
  }, []);

  const closeStockControlModal = useCallback(() => {
    setStockControlRow(null);
  }, []);

  const warehouseNameForModal = useMemo(() => {
    const opt = warehousesOptions.find((o) => o.value === filters.warehouseId);
    return opt ? String(opt.label ?? '') : undefined;
  }, [warehousesOptions, filters.warehouseId]);

  const columns = useMemo(
    () =>
      getPurchasePlanningSettingsColumns({
        onStockControl: openStockControlModal,
      }),
    [openStockControlModal]
  );

  return {
    isPageForbidden,
    isFiltersDisabled,
    getProductsLimitsFiltersDataHandler,
    productCategoriesOptions,
    warehousesOptions,
    unsavedFilters,
    isPageLoading,
    range,
    pagination,
    onSearch,
    onReset,
    columns,
    productsLimitsData: pageData,
    isFetching,
    onPaginate,
    onPageSizeChange,
    onFiltersChange,
    stockControlRow,
    isStockControlModalOpen: stockControlRow !== null,
    closeStockControlModal,
    warehouseNameForModal,
    warehouseId: filters.warehouseId,
  };
};
